import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { Button } from '@chakra-ui/react';
import AdminState from '../AdminState';
import './EditMachineDropDown.css';

export default function SliderFormDropDownExtra({ title, fields, dataFunctionMachineTypes,
  dataFunctionToolCategories, dataFunctionAuto, onSubmit, onDelete, onCancel }) {
  const [submission, setSubmission] = useState({ name: '', machineType: '', categoryID: '' });
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    Promise.all([dataFunctionMachineTypes(), dataFunctionToolCategories(), dataFunctionAuto()])
      .then(([toolResponse, categoryResponse, instance]) => {
        if ([toolResponse, categoryResponse, instance].some(response => response.status !== 200)) throw new Error('load_failed');
        if (!active) return;
        setTools(toolResponse.items || []);
        setCategories(categoryResponse.items || []);
        setSubmission({ name: instance.name, machineType: instance.machineType, categoryID: instance.categoryID });
      }).catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dataFunctionMachineTypes, dataFunctionToolCategories, dataFunctionAuto, retry]);

  // Legacy names remain valid; numeric v2 IDs stay numeric in an untouched save.
  const selectedTool = tools.find(tool => String(tool.id) === String(submission.machineType) || tool.name === submission.machineType);
  const change = (key, value) => {
    setSubmission(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: '' }));
  };
  const submit = async event => {
    event.preventDefault();
    if (pending) return;
    const nextErrors = {};
    fields.forEach(field => {
      const value = String(submission[field.key] ?? '');
      if (!value.trim()) nextErrors[field.key] = `${field.placeholder} is required.`;
      else if (field.validate && !value.match(field.validate)) nextErrors[field.key] = field.errorText;
    });
    if (!selectedTool) nextErrors.machineType = 'Choose a tool.';
    if (!categories.some(category => String(category.categoryID) === String(submission.categoryID))) nextErrors.categoryID = 'Choose a tool category.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const field = Object.keys(nextErrors)[0];
      event.currentTarget.querySelector(`[id="${field}"]`)?.focus();
      return;
    }
    setPending('save');
    setSaveError('');
    try { await onSubmit(submission); }
    catch { setSaveError('Could not save this tool instance. Your changes are still here. Try again.'); }
    finally { setPending(''); }
  };
  const remove = async () => {
    if (pending || !window.confirm('Are you sure you want to delete this tool instance? This action cannot be undone.')) return;
    setPending('delete');
    setSaveError('');
    try { await onDelete(); }
    catch { setSaveError('Could not delete this tool instance. Try again.'); }
    finally { setPending(''); }
  };
  if (loadError) return <AdminState status="error" title="Tool instance could not be loaded"
    description="Try again to load its details, tools, and categories." onRetry={() => setRetry(value => value + 1)} />;
  if (loading) return <AdminState status="loading" title="Loading tool instance details…" />;
  return <form className="SliderFormDropDown SliderFormDropDownExtra" onSubmit={submit} noValidate>
    {saveError && <AdminState status="error" title="Changes could not be completed" description={saveError} mb={0} />}
    {fields.map(field => <TextField fullWidth required key={field.key} id={field.key} name={field.key}
      label={field.placeholder} helperText={errors[field.key]} error={Boolean(errors[field.key])}
      value={submission[field.key]} onChange={event => change(field.key, event.target.value)} disabled={field.disabled || Boolean(pending)} />)}
    <FormControl fullWidth required error={Boolean(errors.machineType)} disabled={Boolean(pending)}>
      <InputLabel id="machineType-label" htmlFor="machineType">{title}</InputLabel>
      <Select labelId="machineType-label" inputProps={{ id: 'machineType', 'aria-describedby': errors.machineType ? 'machineType-error' : undefined }}
        value={selectedTool ? selectedTool.id : ''} onChange={event => change('machineType', event.target.value)}>
        {tools.map(tool => <MenuItem key={tool.id} value={tool.id}>{tool.name}</MenuItem>)}
      </Select>
      {errors.machineType && <FormHelperText id="machineType-error">{errors.machineType}</FormHelperText>}
    </FormControl>
    <FormControl fullWidth required error={Boolean(errors.categoryID)} disabled={Boolean(pending)}>
      <InputLabel id="categoryID-label" htmlFor="categoryID">Tool category</InputLabel>
      <Select labelId="categoryID-label" inputProps={{ id: 'categoryID', 'aria-describedby': errors.categoryID ? 'categoryID-error' : undefined }}
        value={submission.categoryID} onChange={event => change('categoryID', event.target.value)}>
        {categories.map(category => <MenuItem key={category.categoryID} value={category.categoryID}>{category.categoryName}</MenuItem>)}
      </Select>
      {errors.categoryID && <FormHelperText id="categoryID-error">{errors.categoryID}</FormHelperText>}
    </FormControl>
    <div className="admin-form-actions">
      <Button variant="solid" colorScheme="red" onClick={remove} isDisabled={Boolean(pending)} isLoading={pending === 'delete'} aria-label={pending === 'delete' ? 'Deleting…' : undefined}>Delete</Button>
      <div className="admin-form-save-actions">
        {onCancel && <Button variant="outline" onClick={onCancel} isDisabled={Boolean(pending)}>Cancel</Button>}
        <Button type="submit" colorScheme="blue" isDisabled={Boolean(pending)} isLoading={pending === 'save'} aria-label={pending === 'save' ? 'Saving…' : undefined}>Save</Button>
      </div>
    </div>
  </form>;
}
